import { useId, useRef, useState, useEffect } from 'react';

interface CometProps {
  scale?: number;
  tabletScale?: number;
  mobileScale?: number;
  reverse?: boolean;
  duration?: number;
  delay?: number;
  easing?: string;
  className?: string;
}

// Base dimensions (before scaling)
const BASE_WIDTH = 250; // 232px tail + 18px head
const BASE_HEIGHT = 50; // Accommodates glow vertical extent

const MOBILE_BREAKPOINT = 768;
const TABLET_BREAKPOINT = 1024;

const Comet = ({ 
  scale = 1, 
  tabletScale,
  mobileScale,
  reverse = false,
  duration = 1,
  delay = 0,
  easing = 'cubic-bezier(0.16, 1, 0.3, 1)',
  className = ''
}: CometProps) => {
  const id = useId();
  const animationName = `comet-travel-${id.replace(/:/g, '')}`;
  const [isVisible, setIsVisible] = useState(false);
  const [activeScale, setActiveScale] = useState(scale);
  const containerRef = useRef<HTMLDivElement>(null);

  // Handle responsive scale changes
  useEffect(() => {
    const updateScale = () => {
      const width = window.innerWidth;
      if (width < MOBILE_BREAKPOINT && mobileScale !== undefined) {
        setActiveScale(mobileScale);
      } else if (width < TABLET_BREAKPOINT && tabletScale !== undefined) {
        setActiveScale(tabletScale);
      } else {
        setActiveScale(scale);
      }
    };

    updateScale();
    window.addEventListener('resize', updateScale);
    return () => window.removeEventListener('resize', updateScale);
  }, [scale, tabletScale, mobileScale]);
  
  // Scaled dimensions
  const width = BASE_WIDTH * activeScale;
  const height = BASE_HEIGHT * activeScale;

  // Trigger animation only when first entering viewport
  useEffect(() => {
    if (isVisible || !containerRef.current) return;
    
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.2 }
    );
    
    observer.observe(containerRef.current);
    
    return () => observer.disconnect();
  }, [isVisible]);

  return (
    <>
      {/* Container sized to show full comet */}
      <div 
        ref={containerRef}
        className={`relative ${className}`}
        style={{
          width: `${width}px`,
          height: `${height}px`,
          clipPath: reverse ? 'inset(-9999px -10px -9999px -9999px)' : 'inset(-9999px -9999px -9999px -10px)',
        }}
      >
        {/* Animated comet group */}
        <div
          className="absolute top-1/2 -translate-y-1/2"
          style={{
            right: reverse ? 'auto' : `calc(100% + ${30 * activeScale}px)`,
            left: reverse ? `calc(100% + ${30 * activeScale}px)` : 'auto',
            transform: `translateY(-50%) scale(${activeScale}) ${reverse ? 'scaleX(-1)' : ''}`,
            transformOrigin: reverse ? 'left center' : 'right center',
            animation: isVisible
              ? `${animationName} ${duration}s ${easing} ${delay}s forwards`
              : 'none',
          }}
        >
          {/* Long thin gradient tail */}
          <div 
            className="absolute top-1/2 right-full -translate-y-1/2"
            style={{
              width: '232px',
              height: '3px',
              background: 'linear-gradient(to right, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.3) 30%, rgba(255,255,255,0.5) 70%, rgba(255,255,255,1) 100%)',
              clipPath: 'polygon(0 50%, 100% 0, 100% 100%)',
            }}
          />
          {/* Subtle inner glow line */}
          <div 
            className="absolute top-1/2 right-full -translate-y-1/2"
            style={{
              width: '140px',
              height: '1px',
              background: 'linear-gradient(to right, transparent 0%, rgba(255,255,255,0.8) 60%, rgba(255,255,255,1) 100%)',
            }}
          />
          {/* Tight head glow */}
          <div 
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-7 h-7 bg-foreground/40 blur-md rounded-full"
          />
          {/* Comet head - sharp bright center */}
          <div 
            className="relative w-[18px] h-[18px] bg-foreground rounded-full"
            style={{
              boxShadow: '0 0 8px 4px rgba(255,255,255,0.5), 0 0 2px 1px rgba(255,255,255,0.8)',
            }}
          />
          {/* Small cone of light behind comet head */}
          <div 
            className="absolute top-1/2 -translate-y-1/2"
            style={{
              right: '50%',
              width: '60px',
              height: '50px',
              background: 'radial-gradient(ellipse at right center, rgba(255,255,255,0.35) 0%, rgba(255,255,255,0.12) 40%, rgba(255,255,255,0) 70%)',
              filter: 'blur(8px)',
            }}
          />
          {/* Flashlight glow to the left of comet */}
          <div 
            className="absolute top-1/2 -translate-y-1/2 blur-2xl"
            style={{
              right: '50%',
              width: '80px',
              height: '50px',
              background: 'radial-gradient(ellipse at right center, rgba(255,255,255,0.5) 0%, rgba(255,255,255,0.2) 40%, rgba(255,255,255,0) 70%)',
              borderRadius: '60% 40% 55% 45% / 45% 55% 40% 60%',
            }}
          />
        </div>
      </div>

      <style>{`
        @keyframes ${animationName} {
          0% {
            ${reverse ? 'left' : 'right'}: calc(100% + ${30 * activeScale}px);
          }
          100% {
            ${reverse ? 'left' : 'right'}: 0;
          }
        }
      `}</style>
    </>
  );
};

export default Comet;
